import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { NotificationService, SessionService } from '../../core/services';
import { OverviewService } from './services/overview.service';
import { OverviewResult } from './models/overview.model';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  overview: OverviewResult | null = null;
  isLoading = false;
  /** Set when the first load fails; a failed range switch keeps the data already on screen instead. */
  loadFailed = false;

  readonly rangeOptions = [
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
    { days: 90, label: 'Last 90 days' },
    { days: 365, label: 'Last year' },
  ];
  readonly selectedRangeDays = signal(30);
  rangeMenuOpen = false;

  constructor(
    private overviewService: OverviewService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.load(this.selectedRangeDays());
  }

  /** `fallbackDays` is the range already on screen, restored if a range switch fails. */
  private load(days: number, fallbackDays?: number): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.overviewService.getOverview(days)
      .pipe(
        takeUntil(this._destroy$),
        tap(overview => { this.overview = overview; }),
        catchError(error => {
          console.error('Failed to load dashboard overview.', error);
          if (this.overview) {
            const failedLabel = this.rangeLabel().toLowerCase();
            if (fallbackDays) this.selectedRangeDays.set(fallbackDays);
            this.notificationService.error(`Couldn't load the ${failedLabel}. Still showing the ${this.rangeLabel().toLowerCase()}.`);
          } else {
            this.loadFailed = true;
          }
          return EMPTY;
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  retry(): void {
    this.load(this.selectedRangeDays());
  }

  selectRange(days: number): void {
    const previous = this.selectedRangeDays();
    this.rangeMenuOpen = false;
    if (days === previous) return;
    this.selectedRangeDays.set(days);
    this.load(days, previous);
  }

  toggleRangeMenu(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = this.rangeMenuOpen;
    this.closeMenu();
    if (!wasOpen) {
      this.rangeMenuOpen = true;
    }
  }

  /** Bound to (appClickOutside) on the range dropdown wrapper in the
   *  template safe to call even when nothing's open. */
  closeMenu(): void {
    this.rangeMenuOpen = false;
  }

  /** "Good morning" / "Good afternoon" / "Good evening", local to the browser. */
  get greeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }

  get firstName(): string | null {
    const name = this.sessionService.user?.name;
    return name ? name.split(' ')[0] : null;
  }

  /** "Last 30 days" etc., matching whichever rangeOptions entry is selected
   *  not the resolved absolute dates, so it reads the same as the menu
   *  item you picked instead of a computed date range. A computed signal
   *  instead of a getter: a getter re-runs on every change-detection pass
   *  (e.g. a mousemove), a computed only re-runs when selectedRangeDays
   *  itself actually changes. */
  readonly rangeLabel = computed(() => this.rangeOptions.find(opt => opt.days === this.selectedRangeDays())?.label ?? '');

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
